/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: types.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the certificates module.
────────────────────────────────────────────────────────────────*/


export type CertificateType = 'SELF_SIGNED' | 'LETS_ENCRYPT' | 'CUSTOM';
export type CertificateStatus = 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'REVOKED' | 'ERROR';
export type CertificateLastAction =
    | 'created'
    | 'renewed'
    | 'deployed'
    | 'failed'
    | 'issued'
    | 'certbot_failed'
    | 'preflight_failed'
    | 'generation_failed'
    | 'dns_challenge_pending'
    | 'restarting_challenge'
    | (string & {});   // open extension: allows future backend values without TS errors

export interface Certificate {
    id: number;
    uuid: string;
    name: string;
    type: CertificateType;
    commonName: string;
    subjectAltNames: string[];

    // Certificate generation config
    keySize: number;
    validityDays: number;

    // Let's Encrypt config
    acmeAccountEmail?: string | null;
    acmeChallengeType?: string | null;
    autoRenew: boolean;
    renewBeforeDays: number;

    // Certificate metadata (from parsed cert)
    issuer?: string | null;
    serialNumber?: string | null;
    fingerprint?: string | null;
    notBefore?: string | null;
    notAfter?: string | null;

    // Filesystem paths
    certPath?: string | null;
    keyPath?: string | null;
    chainPath?: string | null;
    fullchainPath?: string | null;

    // Status & Lifecycle
    status: CertificateStatus;
    lastAction?: CertificateLastAction | null;
    lastActionAt?: string | null;
    lastActionError?: string | null;
    lastCheckedAt?: string | null;

    // Audit
    tenantId: number;
    createdAt: string;
    updatedAt: string;
    createdBy?: number | null;
    updatedBy?: number | null;
}

export interface CertificateListItem {
    id: number;
    uuid: string;
    name: string;
    type: CertificateType;
    commonName: string;
    status: CertificateStatus;
    issuer?: string | null;
    notBefore?: string | null;
    notAfter?: string | null;
    lastAction?: CertificateLastAction | null;
    lastActionError?: string | null;
    autoRenew?: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface CertificateFormData {
    name: string;
    type: CertificateType;
    commonName: string;
    subjectAltNames: string[];
    keySize: number;
    validityDays: number;
    acmeAccountEmail: string;
    acmeChallengeType: 'http-01' | 'dns-01';
    autoRenew: boolean;
    renewBeforeDays: number;
    certificate: string;
    privateKey: string;
    certificateChain: string;
}

export interface CertificateInfo {
    commonName: string;
    issuer: string;
    serialNumber: string;
    fingerprint: string;
    notBefore: string;
    notAfter: string;
    subjectAltNames: string[];
    isValid: boolean;
    daysUntilExpiry: number;
    keyMatch?: boolean | null;
}

export const DEFAULT_FORM_DATA: CertificateFormData = {
    name: '',
    type: 'SELF_SIGNED',
    commonName: '',
    subjectAltNames: [],
    keySize: 2048,
    validityDays: 365,
    acmeAccountEmail: '',
    acmeChallengeType: 'http-01',
    autoRenew: true,
    renewBeforeDays: 30,
    certificate: '',
    privateKey: '',
    certificateChain: '',
};

export const CERTIFICATE_TYPE_OPTIONS = [
    { value: 'SELF_SIGNED', label: 'Self-Signed' },
    { value: 'LETS_ENCRYPT', label: "Let's Encrypt" },
    { value: 'CUSTOM', label: 'Custom' },
];

export const KEY_SIZE_OPTIONS = [
    { value: 2048, label: '2048 bits' },
    { value: 4096, label: '4096 bits' },
];

export const VALIDITY_OPTIONS = [
    { value: 30, label: '30 days' },
    { value: 90, label: '90 days' },
    { value: 180, label: '180 days' },
    { value: 365, label: '1 year' },
    { value: 730, label: '2 years' },
    { value: 1095, label: '3 years' },
    { value: 1825, label: '5 years' },
    { value: 3650, label: '10 years' },
];

export const ACME_CHALLENGE_OPTIONS = [
    { value: 'http-01', label: 'HTTP-01 (Web Server)' },
    { value: 'dns-01', label: 'DNS-01 (DNS Provider)' },
];
